import { lazy, Suspense, useEffect } from "react";
import { Redirect, Route, Switch } from "wouter";
import { useAuth } from "./auth";
import { Button } from "./components";
import { AppLayout } from "./layout";
import { AppShellSkeleton, RouteSkeleton } from "./routeSkeleton";

const OverviewPage = lazy(() =>
  import("./overviewPage").then((module) => ({ default: module.OverviewPage })),
);
const DevicesPage = lazy(() =>
  import("./pages").then((module) => ({ default: module.DevicesPage })),
);
const EventsPage = lazy(() =>
  import("./pages").then((module) => ({ default: module.EventsPage })),
);
const WorkflowsPage = lazy(() =>
  import("./pages").then((module) => ({ default: module.WorkflowsPage })),
);
const DeploymentsPage = lazy(() =>
  import("./pages").then((module) => ({ default: module.DeploymentsPage })),
);
const SoftwareUpdatesPage = lazy(() =>
  import("./softwareUpdatesPage").then((module) => ({ default: module.SoftwareUpdatesPage })),
);
const AnalyticsPage = lazy(() =>
  import("./pages").then((module) => ({ default: module.AnalyticsPage })),
);

const GalleryPage = lazy(() =>
  import("./galleryPage").then((module) => ({ default: module.GalleryPage })),
);
const SettingsPage = lazy(() =>
  import("./settingsPage").then((module) => ({ default: module.SettingsPage })),
);
const NotificationPage = lazy(() =>
  import("./notificationPage").then((module) => ({ default: module.NotificationPage })),
);
const NotificationSettingsPage = lazy(() =>
  import("./notificationPage").then((module) => ({ default: module.NotificationSettingsPage })),
);

const GoogleMark = () => (
  <svg className="sign-in-google-mark" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303C33.654 32.658 29.239 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.155 7.959 3.041l5.657-5.657C34.054 6.053 29.28 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.155 7.959 3.041l5.657-5.657C34.054 6.053 29.28 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.178 0 9.86-1.977 13.409-5.193l-6.19-5.238C29.144 35.091 26.678 36 24 36c-5.218 0-9.62-3.317-11.283-7.946l-6.522 5.025C9.503 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.085 5.569l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const SignIn = () => {
  const { signIn, error } = useAuth();

  return (
    <main className="sign-in">
      <div className="sign-in-ambient" aria-hidden="true">
        <span className="sign-in-wave sign-in-wave-one" />
        <span className="sign-in-wave sign-in-wave-two" />
        <span className="sign-in-wave sign-in-wave-three" />
        <span className="sign-in-wave sign-in-wave-four" />
      </div>
      <section
        className="sign-in-card"
        aria-labelledby="sign-in-title"
      >
        <div className="sign-in-card-header">
          <div className="sign-in-brand-lockup">
            <span className="sign-in-mark" aria-hidden="true">
              <img src="/boothworks-pp.jpg" alt="" />
            </span>
            <span className="sign-in-brand-name">BoothWorks</span>
          </div>
        </div>
        <div className="sign-in-copy">
          <h1 id="sign-in-title">Welcome Back</h1>
          <p className="sign-in-lede">
            Continue to your BoothWorks Control Centre.
          </p>
        </div>
        {error && (
          <div className="sign-in-error" role="alert" aria-live="assertive">
            {error}
          </div>
        )}
        <Button
          tone="primary"
          icon={<GoogleMark />}
          onClick={() => void signIn()}
          aria-label="Continue with Google to open BoothWorks Control Centre"
        >
          Continue with Google
        </Button>
        <div className="sign-in-divider" aria-hidden="true">
          <span />
          <small>Secure account access</small>
          <span />
        </div>
        <p className="sign-in-footnote">
          Use your approved Google account. Stay signed in for 90 days on this browser.
        </p>
      </section>
    </main>
  );
};

export const App = () => {
  const { user, accountId, loading } = useAuth();
  const signedIn = Boolean(user && accountId);
  useEffect(() => {
    if (!signedIn) return;
    // Fetch the most used pages in the background so moving between them is instant.
    const prefetch = () => {
      void import("./overviewPage");
      void import("./galleryPage");
      void import("./settingsPage");
    };
    if ("requestIdleCallback" in window) {
      const handle = window.requestIdleCallback(prefetch, { timeout: 4000 });
      return () => window.cancelIdleCallback(handle);
    }
    const timer = setTimeout(prefetch, 1500);
    return () => clearTimeout(timer);
  }, [signedIn]);
  if (loading) return <AppShellSkeleton />;
  if (!user || !accountId) return <SignIn />;
  return (
    <AppLayout>
      <Suspense fallback={<RouteSkeleton />}>
        <Switch>
          <Route path="/" component={OverviewPage} />
          <Route path="/devices" component={DevicesPage} />
          <Route path="/events" component={EventsPage} />
          <Route path="/galleries" component={GalleryPage} />
          <Route path="/workflows" component={WorkflowsPage} />
          <Route path="/deployments" component={DeploymentsPage} />
          <Route path="/software-updates" component={SoftwareUpdatesPage} />
          <Route path="/analytics" component={AnalyticsPage} />
          <Route path="/notifications" component={NotificationPage} />
          <Route path="/settings/notifications" component={NotificationSettingsPage} />
          <Route path="/settings" component={SettingsPage} />
          <Route path="/security">
            <Redirect to="/settings#security" />
          </Route>
          <Route>
            <Redirect to="/" />
          </Route>
        </Switch>
      </Suspense>
    </AppLayout>
  );
};
